<template>
  <div class="video-recorder">
    <VideoRecorderError v-if="!isVideoStreamLoaded">
      <template v-if="fsm.currentState === 'streamPending'">
        <div class="video-recorder__spinner">
          <SimpleSpinner />
        </div>
      </template>
      <template v-if="fsm.currentState === 'permissionDenied'">
        <NoMediaAccess @retry="() => fsm.send('REQUEST_STREAM')" />
      </template>
      <template v-else-if="fsm.currentState === 'devicesNotFound'">
        {{ c('video_recorder.video_or_audio_disconnected') }}
      </template>
      <template v-else-if="fsm.currentState === 'loadingVideo'">
        <SimpleSpinner />
      </template>
    </VideoRecorderError>

    <div
      v-if="(warningMessage || warningMessageOverride) && fsm.currentState === 'recording'"
      class="video-recorder__message-container"
    >
      <WarningMessage>{{ warningMessageOverride ?? warningMessage }}</WarningMessage>
    </div>
    <PreviewVideo
      v-show="isVideoStreamLoaded"
      ref="previewVideo"
      class="video-recorder__recorder"
      :stream="stream"
      :mirrored="mirrored"
      @loaded="() => fsm.send('PREVIEW_VIDEO_LOADED')"
    />
  </div>
</template>
<script lang="ts">
import { useFSM } from '@fsmify/vue'
import { computed, defineComponent, type PropType, reactive, ref, watch } from 'vue'

import { c } from '../../../intl'
import SimpleSpinner from '../../../shared/components/dumb/SimpleSpinner.vue'
import { useAudioLevelMonitor } from '../../composables/useAudioLevelMonitor'
import { useMediaRecorder } from '../../composables/useMediaRecorder'
import { useRequestPermissions } from '../../composables/useRequestPermissions'
import { useUserMedia, type UseUserMediaConstraints } from '../../composables/useUserMedia'

import NoMediaAccess from './NoMediaAccess.vue'
import PreviewVideo from './PreviewVideo.vue'
import VideoRecorderError from './VideoRecorderError.vue'
import WarningMessage from './WarningMessage.vue'

export default defineComponent({
  components: { SimpleSpinner, NoMediaAccess, PreviewVideo, WarningMessage, VideoRecorderError },
  props: {
    mediaConstraints: {
      type: Object as PropType<UseUserMediaConstraints>,
      required: true,
    },
    isRecording: {
      type: Boolean,
      required: true,
    },
    warningMessageOverride: {
      type: String,
      required: false,
      default: null,
    },
  },
  emits: {
    recorded: (_blob: Blob) => true,
    streamLoaded: (_loaded: false | { videoDeviceId: string; audioDeviceId: string }) => true,
    errorStartingRecording: (_error: unknown) => true,
  },
  setup(props, { emit }) {
    const warningMessage = ref<string | undefined>(undefined)
    const previewVideo = ref<typeof PreviewVideo | null>(null)
    const permissions = useRequestPermissions()

    watch(
      () => props.mediaConstraints,
      async () => {
        const videoElement = previewVideo.value?.videoPreview
        if (!videoElement || !('srcObject' in videoElement)) return

        videoElement.srcObject = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: props.mediaConstraints.video.deviceId,
          },
          audio: {
            deviceId: props.mediaConstraints.audio.deviceId,
          },
        })
      },
    )

    const mirrored = computed(() =>
      'facingMode' in props.mediaConstraints.video
        ? props.mediaConstraints.video.facingMode !== 'environment'
        : true,
    )

    const fsm = useFSM({
      initialState: 'streamPending',
      states: {
        streamPending: {
          STREAM_READY: 'loadingVideo',
          PERMISSION_DENY: 'permissionDenied',
        },
        loadingVideo: { PREVIEW_VIDEO_LOADED: 'idle' },
        idle: {
          START_RECORDING: 'recording',
          FLIP_CAMERA: 'loadingVideo',
        },
        recording: {
          STOP_RECORDING: 'idle',
          ERROR: 'permissionDenied',
        },
        permissionDenied: {
          REQUEST_STREAM: 'streamPending',
        },
        devicesNotFound: {
          STREAM_READY: 'loadingVideo',
          REQUEST_STREAM: 'streamPending',
        },
      },
      globalEvents: {
        DEVICES_MISSING: 'devicesNotFound',
      },
    })

    const userMedia = useUserMedia(computed(() => props.mediaConstraints))

    const audioLevelMonitor = useAudioLevelMonitor({
      minDb: 5,
      checkFrequencyMs: 100,
      stream: userMedia.stream,
      thresholdMs: 5000,
      isAboveMinDBByDefault: true,
    })

    watch(userMedia.state, (userMediaState) => {
      switch (userMediaState) {
        case 'permissionPending':
          return fsm.send('REQUEST_STREAM')
        case 'initialised':
          return fsm.send('STREAM_READY')
        case 'permissionDenied':
          return fsm.send('PERMISSION_DENY')
        case 'devicesMissing':
          return fsm.send('DEVICES_MISSING')
      }
    })

    watch(permissions, (state) => {
      switch (state) {
        case 'denied':
          return fsm.send('PERMISSION_DENY')
        case 'granted':
          return fsm.send('REQUEST_STREAM')
      }
    })

    watch(audioLevelMonitor.isAboveMinDB, (isAboveMinDB) => {
      if (props.warningMessageOverride) return
      if (!isAboveMinDB) return (warningMessage.value = c('video_recorder.cannot_hear'))
      warningMessage.value = undefined
    })

    const isVideoStreamLoaded = computed(() =>
      ['idle', 'recording'].includes(fsm.currentState.value),
    )

    watch(isVideoStreamLoaded, () => {
      if (!isVideoStreamLoaded.value) {
        emit('streamLoaded', false)
        return
      }

      if (!userMedia.stream.value)
        throw new Error('expect stream to be defined when the stream is loaded')

      const videoDeviceId = userMedia.stream.value.getVideoTracks()[0]?.getSettings().deviceId
      const audioDeviceId = userMedia.stream.value.getAudioTracks()[0]?.getSettings().deviceId

      if (!videoDeviceId || !audioDeviceId)
        throw new Error('expect both video and audio tracks to be defined')

      emit('streamLoaded', { videoDeviceId, audioDeviceId })
    })

    watch(
      () => props.isRecording,
      (isRecording) => fsm.send(isRecording ? 'START_RECORDING' : 'STOP_RECORDING'),
      { immediate: true },
    )

    const recorder = useMediaRecorder()
    fsm.onAfterEnterState('recording', () => {
      if (!userMedia.stream.value) return
      try {
        recorder.start(userMedia.stream.value)
      } catch (e) {
        fsm.send('ERROR')
        emit('errorStartingRecording', e)
      }
    })
    fsm.onBeforeLeaveState('recording', async ({ event }) => {
      const blob = await recorder.stop()
      if (event === 'STOP_RECORDING') {
        emit('recorded', blob)
        userMedia.stream.value?.getTracks().forEach((track) => track.stop())
      }
    })

    return {
      fsm: reactive(fsm),
      stream: userMedia.stream,
      mirrored,
      warningMessage,
      isVideoStreamLoaded,
      previewVideo,
      c,
    }
  },
})
</script>
<style lang="postcss">
.video-recorder {
  position: relative;

  &__spinner {
    img {
      width: 2rem;
      height: 2rem;
    }
  }

  &__recorder {
    border-radius: 0.5rem;
  }

  &__message-container {
    position: absolute;
    z-index: 1;
    top: 12px;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
  }
}
</style>
